import { debounce } from '@app/utils/common';
import { loggerFor } from '@app/utils/logger';
import { BehaviorSubject, Observable, of } from 'rxjs';

interface Timeout {
  dispose: () => void;
  id: string;
  resolve: () => void;
  timeoutId: number | null;
}

/**
 * Queue that clears values after a specific amount of time after they have been replaced by another
 * value.
 */
export class AutoClearingQueue<T> {
  private _timeouts$ = new BehaviorSubject<Timeout[]>([]);

  get timeouts$(): Observable<Timeout[]> {
    return this._timeouts$.asObservable();
  }

  private log = loggerFor('AutoClearingQueue');

  private data: Record<string, T> = {};
  private timeouts: Timeout[] = [];

  private emitTimeouts = debounce(() => {
    this._timeouts$.next(this.timeouts);
  }, 10);

  /**
   * @param count Size of the queue
   * @param ttl Duration in ms after which entries will be cleared
   */
  constructor(
    private count: number,
    private ttl: number
  ) {}

  /**
   * Clear queue and immediately resolve "cleared" promises.
   */
  clear(): void {
    this.timeouts.forEach(({ timeoutId, resolve }) => {
      window.clearTimeout(timeoutId);
      resolve();
    });

    this.timeouts = [];
    this.emitTimeouts();
  }

  /**
   * Remove the current value and immediately resolve "cleared" promise.
   */
  clearActive(): void {
    const activeTimeout = this.timeouts.pop();

    if (activeTimeout != null) {
      this.removeEntry(activeTimeout);
      delete this.data[activeTimeout.id];
    }

    this.emitTimeouts();
  }

  /**
   * Delete an entry that matches this ID. Does nothing if the ID does not exist.
   */
  delete(id: string): void {
    if (this.has(id)) {
      this.log.debug(`[ ${id} ] Delete entry`);

      this.removeEntry(
        this.timeouts.find((timeout) => timeout.id === id),
        true
      );
    }
  }

  /**
   * Check if the queue knows the given id.
   */
  has(id: string): boolean {
    return this.data[id] != null;
  }

  /**
   * Set the given data as the active value. The currently active value will be marked as inactive
   * and the counter to remove it is started.
   * @returns Promise that is resolved after the data is removed from the queue.
   */
  push(data: T, id: string): Promise<string> {
    this.log.debug(`[ ${id} ] push new value`);

    if (this.data[id] != null) {
      this.timeouts
        .filter((timeout) => timeout.id === id)
        .forEach((timeout) => {
          timeout.dispose();
        });
    }

    this.data[id] = data;

    if (this.timeouts.length >= this.count) {
      this.removeEntry(this.timeouts[0]);
    }

    if (this.timeouts.length > 0) {
      this.startRemovalTimeout(this.timeouts[this.timeouts.length - 1]);
    }

    return new Promise((resolve) => {
      const timeout: Timeout = {
        dispose: () => {
          this.log.debug(`[ ${timeout.id} ] dispose`);

          if (timeout.timeoutId != null) {
            window.clearTimeout(timeout.timeoutId);
          }

          this.timeouts = this.timeouts.filter((_) => _ !== timeout);
        },
        id,
        resolve: () => {
          this.log.debug(`[ ${timeout.id} ] resolve`);
          this.data[id] = undefined;
          resolve(id);
        },
        timeoutId: null,
      };

      this.timeouts.push(timeout);
      this.emitTimeouts();
    });
  }

  /**
   * Get stored value for the given id or null if the id does not exist.
   */
  retrieve(id: string): T | null {
    return this.data[id] ?? null;
  }

  /**
   * Get an observable with the value of the given id or null if the id does not exist.
   */
  retrieve$(id: string): Observable<T> | null {
    const value = this.retrieve(id);

    return value != null ? of(value) : null;
  }

  private removeEntry(timeout: Timeout, force?: boolean): void {
    // If the timeoutId is set then the instance is waiting to be cleared. In this case we can
    // clear the timeout immediately.
    if (timeout.timeoutId != null || force) {
      timeout.dispose();
      timeout.resolve();
    }

    this.timeouts = this.timeouts.filter((_) => _ !== timeout);
    this.emitTimeouts();
  }

  private startRemovalTimeout(timeout: Timeout): void {
    timeout.timeoutId = window.setTimeout(() => {
      this.removeEntry(timeout);
    }, this.ttl);
  }
}
